import React, { createContext, useContext, useMemo } from "react";
import { useWebRTC } from "../hooks/useWebRTC";

const WebRTCContext = createContext(null);

/**
 * Legacy P2P signaling socket (/api/webrtc/ws/{userId}).
 *
 * Nothing in the app consumes this context (no `useWebRTCContext` callers) and the hook has
 * no UI/sound side effects, yet it opened a WebSocket — reconnecting with backoff forever —
 * in every authenticated tab. Calls run through the unified workspace socket and Synex
 * Connect. The socket is therefore opt-in; set REACT_APP_ENABLE_LEGACY_WEBRTC_SIGNALING=true
 * to restore the previous always-connected behaviour. The provider and hook API are kept.
 */
export const LEGACY_WEBRTC_SIGNALING_ENABLED =
  process.env.REACT_APP_ENABLE_LEGACY_WEBRTC_SIGNALING === "true";

export function WebRTCProvider({ userId, children }) {
  // useWebRTC does not connect when userId is falsy.
  const webrtc = useWebRTC(LEGACY_WEBRTC_SIGNALING_ENABLED ? userId : null);
  const value = useMemo(() => webrtc, [webrtc]);

  return (
    <WebRTCContext.Provider value={value}>
      {children}
    </WebRTCContext.Provider>
  );
}

export function useWebRTCContext() {
  const ctx = useContext(WebRTCContext);
  if (!ctx) {
    throw new Error("useWebRTCContext must be used inside WebRTCProvider");
  }
  return ctx;
}
